import { z } from 'zod';

import { CivilDateDto } from './civilDate';

export const SetupWalletInstrumentDto = z.object({
  // omitted → the BE resolves the processor that will charge this owner
  // (org billing plan / premium subscription / newest card — R-AUD-91);
  // flows that know their processor (premium plan, booking event) pass it
  provider: z.enum(['stripe', 'payspot']).optional(),
  successUrl: z.string().url().optional(),
  cancelUrl: z.string().url().optional(),
});
export type ApiSetupWalletInstrumentDto = z.infer<
  typeof SetupWalletInstrumentDto
>;

// The query parameter of the hosted card-setup success URL that carries the Checkout
// Session id. Never `session_id`: Cloud Armor's session-fixation rule (OWASP CRS 943110)
// refuses a parameter of that name arriving with an off-site Referer, which is every
// return from Stripe (found 2026-10-22: the card was saved, the page answered 403).
export const WALLET_SETUP_SESSION_PARAM = 'checkout_session';

// self-heal on return from hosted setup (R-AUD-53): FE returns the session id
// from the success URL so BE can fetch the outcome without waiting for the webhook
export const ResolveWalletSetupDto = z.object({
  sessionId: z.string().min(1).max(255),
});
export type ApiResolveWalletSetupDto = z.infer<typeof ResolveWalletSetupDto>;

export const MarketplaceCheckoutDto = z.object({
  marketplaceItemId: z.string().uuid(),
  // The seller's CURRENT Terms of Use version the buyer reviewed and checked
  // the acceptance box for. Checked against the seller org's actual current
  // version server-side (see `MarketplaceCheckoutService.checkout`) — a stale
  // id means the terms changed under the buyer mid-flow, not that they agreed.
  acceptedTermsVersionId: z.string().uuid(),
  successUrl: z.string().url().optional(),
  cancelUrl: z.string().url().optional(),
});
export type ApiMarketplaceCheckoutDto = z.infer<typeof MarketplaceCheckoutDto>;

export const PremiumSubscribeDto = z.object({
  planId: z.string().uuid(),
});
export type ApiPremiumSubscribeDto = z.infer<typeof PremiumSubscribeDto>;

// Q8/Task 6 — monthly ↔ yearly on the personal premium subscription. Only the
// two intervals the premium catalog is built on (`PremiumPlan.interval` is
// MONTH | YEAR): day/week exist in `BillingInterval` for other sources and
// accepting them here would schedule a switch to a plan that can never exist.
export const PremiumChangeIntervalDto = z.object({
  interval: z.enum(['month', 'year']),
});
export type ApiPremiumChangeIntervalDto = z.infer<
  typeof PremiumChangeIntervalDto
>;

export const RequestRecipientOnboardingDto = z.object({
  returnUrl: z.string().url().optional(),
  refreshUrl: z.string().url().optional(),
  // spec 2026-09-21 D3: the Stripe account's country (ISO2), independent of
  // the org's own country; defaults to the org country on the backend
  accountCountry: z
    .string()
    .regex(/^[A-Z]{2}$/)
    .optional(),
});
export type ApiRequestRecipientOnboardingDto = z.infer<
  typeof RequestRecipientOnboardingDto
>;

// PaySpot MT101 `Orders[]` beneficiary — Payment Service spec v6.2.1: name
// A(35), address A(35), city A(20), all Mandatory. A payment order missing any
// of them is refused with errorCode 700970 (Sentry MYFITWORLD-V2-BACKEND-41).
// THE one definition: the admin DTO below, onboarding activation, marketplace
// checkout and the admin form all ask it.
export const PAYSPOT_BENEFICIARY_MAX_LENGTH = {
  beneficiaryName: 35,
  beneficiaryAddress: 35,
  beneficiaryCity: 20,
} as const;

export const PaySpotBeneficiaryFieldsDto = z.object({
  beneficiaryName: z
    .string({
      required_error: 'VALIDATION__Required',
      invalid_type_error: 'VALIDATION__Required',
    })
    .trim()
    .min(1, 'VALIDATION__Required')
    .max(
      PAYSPOT_BENEFICIARY_MAX_LENGTH.beneficiaryName,
      'VALIDATION__Max35CharLimit',
    ),
  beneficiaryAddress: z
    .string({
      required_error: 'VALIDATION__Required',
      invalid_type_error: 'VALIDATION__Required',
    })
    .trim()
    .min(1, 'VALIDATION__Required')
    .max(
      PAYSPOT_BENEFICIARY_MAX_LENGTH.beneficiaryAddress,
      'VALIDATION__Max35CharLimit',
    ),
  beneficiaryCity: z
    .string({
      required_error: 'VALIDATION__Required',
      invalid_type_error: 'VALIDATION__Required',
    })
    .trim()
    .min(1, 'VALIDATION__Required')
    .max(
      PAYSPOT_BENEFICIARY_MAX_LENGTH.beneficiaryCity,
      'VALIDATION__Max20CharLimit',
    ),
});

export type PaySpotBeneficiaryField =
  keyof typeof PAYSPOT_BENEFICIARY_MAX_LENGTH;

/** The fields PaySpot would refuse (missing, blank or too long); `[]` = complete. */
export const paySpotBeneficiaryProblems = (fields: {
  beneficiaryAddress?: null | string;
  beneficiaryCity?: null | string;
  beneficiaryName?: null | string;
}): PaySpotBeneficiaryField[] => {
  const result = PaySpotBeneficiaryFieldsDto.safeParse({
    beneficiaryName: fields.beneficiaryName ?? '',
    beneficiaryAddress: fields.beneficiaryAddress ?? '',
    beneficiaryCity: fields.beneficiaryCity ?? '',
  });
  if (result.success) return [];
  return result.error.issues
    .map((issue) => issue.path[0] as PaySpotBeneficiaryField)
    .filter((field, index, all) => all.indexOf(field) === index);
};

// R-AUD-69 superadmin ops tranzicije PaySpot onboardinga
export const AdminOnboardingBeneficiaryDto = PaySpotBeneficiaryFieldsDto.extend(
  {
    beneficiaryAccount: z.string().min(1).max(40),
    contractSignedAt: z.coerce.date().optional(),
  },
);
export type ApiAdminOnboardingBeneficiaryDto = z.infer<
  typeof AdminOnboardingBeneficiaryDto
>;

export const AdminOnboardingReasonDto = z.object({
  reason: z.string().min(1).max(500),
});
export type ApiAdminOnboardingReasonDto = z.infer<
  typeof AdminOnboardingReasonDto
>;

// §14.5 platform billing setup — the package is chosen FROM THE CATALOG (PlatformPackages)
export const PlatformBillingSetupDto = z.object({
  packageKey: z.string().min(1).max(64),
  provider: z.enum(['stripe', 'payspot']).optional(),
});
export type ApiPlatformBillingSetupDto = z.infer<
  typeof PlatformBillingSetupDto
>;

export const PlatformDiscountDto = z
  .object({
    percentOff: z.number().int().min(1).max(100),
    scope: z.enum(['per_seat', 'total']).optional(),
    startsAt: CivilDateDto.optional(),
    expiresAt: CivilDateDto.optional(),
    note: z.string().max(500).optional(),
  })
  // An inverted pair is stored happily and then covers zero days — a discount
  // that silently never applies. Both bounds are optional, so only a PAIR can
  // be inverted; equal bounds are a legitimate single-day window. Both are
  // normalized civil days by now, so a string compare is a day compare.
  .refine(
    ({ expiresAt, startsAt }) =>
      !startsAt || !expiresAt || startsAt <= expiresAt,
    { message: 'VALIDATION__InvalidRange', path: ['expiresAt'] },
  );
export type ApiPlatformDiscountDto = z.infer<typeof PlatformDiscountDto>;

/**
 * Task 7.2 — switch "Free analytics" on (or extend it) for an organization.
 * The platform user only gives the reason: the grant is always 100 % off and
 * always runs from today for one calendar month (the server decides both).
 */
export const PlatformFreeAnalyticsDto = z.object({
  note: z.string().trim().min(1).max(255),
});
export type ApiPlatformFreeAnalyticsDto = z.infer<
  typeof PlatformFreeAnalyticsDto
>;

export const PlatformGraceDto = z.object({
  days: z.number().int().min(1).max(60),
});
export type ApiPlatformGraceDto = z.infer<typeof PlatformGraceDto>;

// Pricing v2 — the owner chooses a payment term; the stair follows the count
export const PlatformStairTermDto = z.object({
  termMonths: z.union([
    z.literal(1),
    z.literal(3),
    z.literal(6),
    z.literal(12),
  ]),
});
export type ApiPlatformStairTermDto = z.infer<typeof PlatformStairTermDto>;

// Pricing v2 (D5/D12) — a platform user sets an organization's stair
// tolerance: clients over the current stair's cap before it moves up
export const PlatformToleranceDto = z.object({
  tolerancePercent: z.number().int().min(0).max(50),
});
export type ApiPlatformToleranceDto = z.infer<typeof PlatformToleranceDto>;

// Pricing v2 — the platform stair-changes list. `tenantId` defaults to the
// request's tenant; another one is a super-admin crossing; 'all' is refused
export const StairChangesQueryDto = z.object({
  tenantId: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});
export type ApiStairChangesQueryDto = z.infer<typeof StairChangesQueryDto>;
